import { useEffect, useState } from 'react';
import {
  getFinancialYear, getFinancialYears, getFolders, getMeetings, getMembers, getOrganizations, getPaymentPeriods,
  getPosts, getProposals,
} from '../services/ApiService';
import { DATA_CHANGED_EVENT } from '../services/BackendApiClient';
import type { GuideSnapshot } from './guideSteps';

async function loadSnapshot(orgId: string): Promise<GuideSnapshot | null> {
  const [orgs, members, posts, meetings, paymentPeriods, financialYears, folders, proposals] = await Promise.all([
    getOrganizations(), getMembers(orgId), getPosts(orgId), getMeetings(orgId), getPaymentPeriods(orgId),
    getFinancialYears(orgId), getFolders(orgId), getProposals(orgId),
  ]);
  const org = orgs.find(o => o.id === orgId);
  if (!org) return null;
  const latest = [...financialYears].sort((a, b) => b.year - a.year)[0];
  const latestYear = latest ? await getFinancialYear(orgId, latest.id) : null;
  return { org, members, posts, meetings, paymentPeriods, financialYears, latestYear, folders, proposals };
}

// Loads once for the badge and welcome prompt; follows data changes only while the guide is open (`live`),
// so page visits that write (e.g. marking a section seen) don't refetch everything.
export function useGuideSnapshot(orgId: string | null, live: boolean): GuideSnapshot | null {
  const [snapshot, setSnapshot] = useState<GuideSnapshot | null>(null);

  useEffect(() => setSnapshot(null), [orgId]);

  useEffect(() => {
    if (!orgId) return;
    let latestRequest = 0;
    const refresh = () => {
      const request = ++latestRequest;
      loadSnapshot(orgId)
        .then(next => { if (request === latestRequest) setSnapshot(next); })
        .catch(() => {});
    };
    refresh();
    if (live) window.addEventListener(DATA_CHANGED_EVENT, refresh);
    return () => {
      latestRequest = -1;
      window.removeEventListener(DATA_CHANGED_EVENT, refresh);
    };
  }, [orgId, live]);

  return snapshot;
}
